// @ts-nocheck
import * as React from "react";
import { Ic, apiErrorMessage } from "../screens/common";
import { useEscapeKey } from "./useEscapeKey";

// 기관 병합 모달. RegionMergeModal과 동일 패턴.
// source의 모든 매니저가 target으로 이관되고, services 태그가 dedupe·머지된 후
// source는 삭제됨. 되돌릴 수 없는 작업.

async function jsonFetch(url, opts = {}) {
  const res = await fetch(url, { credentials: "same-origin", ...opts });
  let body = null;
  try { body = await res.json(); } catch { /* non-JSON */ }
  if (!res.ok) {
    const err = new Error(body?.details?.join(", ") || body?.error || `HTTP ${res.status}`);
    err.status = res.status;
    if (body?.error) err.code = body.error;
    throw err;
  }
  return body;
}

export function OrganizationMergeModal({ source, allOrganizations, onClose, onMerged }) {
  useEscapeKey(onClose);
  const [targetId, setTargetId] = React.useState("");
  const [impact, setImpact] = React.useState(null);
  const [impactLoading, setImpactLoading] = React.useState(true);
  const [submitting, setSubmitting] = React.useState(false);
  const [err, setErr] = React.useState(null);

  // 후보 = source 자신을 제외한 모든 기관. 카테고리/이름순.
  const candidates = React.useMemo(() =>
    (allOrganizations || [])
      .filter(o => o.id !== source.id)
      .sort((a, b) => (a.name || "").localeCompare(b.name || "", "ko")),
    [allOrganizations, source.id]
  );

  // 미리보기: 이관될 매니저 수. deletion_impact 재사용 — 같은 정보를 제공.
  React.useEffect(() => {
    setImpactLoading(true);
    fetch(`/api/v1/organizations/${source.id}/deletion_impact`, { credentials: "same-origin" })
      .then(r => r.ok ? r.json() : Promise.reject(new Error(`오류 (${r.status})`)))
      .then(j => setImpact(j.impact || {}))
      .catch(() => setImpact({}))
      .finally(() => setImpactLoading(false));
  }, [source.id]);

  const target = React.useMemo(
    () => candidates.find(o => String(o.id) === String(targetId)),
    [candidates, targetId]
  );

  const submit = async (e) => {
    if (e) e.preventDefault();
    setErr(null);
    if (!target) return setErr("흡수할 대상 기관을 선택해주세요.");
    setSubmitting(true);
    try {
      const updated = await jsonFetch(`/api/v1/organizations/${source.id}/merge_into`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ target_id: target.id }),
      });
      onMerged?.(updated);
    } catch (e) {
      const msg = e.status === 401 ? "로그인이 필요합니다."
                : e.status === 403 ? "시스템 관리자 권한이 필요합니다."
                : apiErrorMessage(e);
      setErr(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div onClick={onClose}
      style={{position:'fixed', inset:0, background:'rgba(20,16,12,0.4)', display:'grid', placeItems:'center', zIndex:50}}>
      <form onSubmit={submit} onClick={e=>e.stopPropagation()} className="card" style={{width:560, padding:0}}>
        <div style={{padding:'16px 22px', borderBottom:'1px solid var(--border-soft)', display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:12}}>
          <div>
            <div className="h3">기관 병합</div>
            <div className="muted" style={{fontSize:11, marginTop:2}}>
              <b style={{color:'var(--ink-2)'}}>{source.name}</b>({source.category})을(를) 다른 기관으로 흡수합니다. 이 작업은 되돌릴 수 없습니다.
            </div>
          </div>
          <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>{Ic.close}</button>
        </div>

        {err && (
          <div role="alert" style={{margin:'14px 22px 0', padding:'10px 14px', background:'var(--rose-50)', color:'var(--rose-500)', borderRadius:10, fontSize:12.5}}>
            {err}
          </div>
        )}

        <div style={{padding:22, display:'grid', gap:16}}>
          <div>
            <div className="label">흡수할 대상 기관 *</div>
            {candidates.length === 0 ? (
              <div className="muted" style={{fontSize:12, padding:'8px 0'}}>병합 가능한 다른 기관이 없습니다.</div>
            ) : (
              <select className="select" value={targetId} onChange={e=>setTargetId(e.target.value)} required>
                <option value="">선택…</option>
                {candidates.map(o => (
                  <option key={o.id} value={o.id}>
                    {o.name} · {o.category}{o.address ? ` — ${o.address}` : ""}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div>
            <div className="label">이동될 자원</div>
            <div style={{padding:'10px 14px', background:'var(--bg-sub)', borderRadius:10}}>
              {impactLoading ? (
                <div className="muted" style={{fontSize:12}}>집계 중…</div>
              ) : (
                <div style={{textAlign:'center'}}>
                  <div className="muted" style={{fontSize:10.5, fontWeight:600}}>소속 케어매니저</div>
                  <div className="mono" style={{fontSize:22, fontWeight:800, color:'var(--ink-1)', marginTop:2}}>
                    {impact?.care_managers ?? 0}
                  </div>
                </div>
              )}
              {target && (
                <div className="muted" style={{fontSize:11, marginTop:10, paddingTop:10, borderTop:'1px solid var(--border-soft)'}}>
                  매니저는 모두 <b style={{color:'var(--ink-2)'}}>{target.name}</b>으로 소속이 이동하고,
                  소스의 제공 서비스 태그는 대상 기관에 합쳐집니다(중복 제거).
                </div>
              )}
            </div>
          </div>
        </div>

        <div style={{padding:'14px 22px', borderTop:'1px solid var(--border-soft)', display:'flex', justifyContent:'flex-end', gap:8}}>
          <button type="button" className="btn btn-ghost btn-md" onClick={onClose}>취소</button>
          <button type="submit" className="btn btn-primary btn-md"
            disabled={submitting || !targetId || candidates.length === 0}
            style={{background: submitting ? undefined : 'var(--rose-500)'}}>
            {submitting ? '병합 중…' : '병합 진행'}
          </button>
        </div>
      </form>
    </div>
  );
}
